import React, { useCallback, useContext, useRef, useState } from "react";
import { Container, Form, Button } from "react-bootstrap";
import MaterialTable from "material-table";
import { BsSearch, BsXCircle, BsChevronDown, BsChevronUp, BsArrowClockwise, BsBoxSeam, BsExclamationCircle } from "react-icons/bs";
import ApiService from "../ApiService";
import { parseAxiosError } from "../util/parserHelper";
import { SnackbarContext } from "./AlertSystem";
import {
  ADVANCED_SEARCH_FIELDS,
  ORDER_SEARCH_DEFAULT_LIMIT,
  ORDER_SEARCH_MAX_LIMIT,
  ORDER_SEARCH_MIN_LIMIT,
  buildOrderSearchParams,
  displayValue,
  formatDate,
  formatDateTime,
  validateOrderSearchFilters,
} from "../util/clinicianOrderSearch";
import "./OrderKitLookupPage.css";

const EMPTY_FILTERS = {
  orderId: "",
  kitId: "",
  partnerId: "",
  pharmacyPartner: "",
  fromDate: "",
  toDate: "",
  limit: String(ORDER_SEARCH_DEFAULT_LIMIT),
};

const TrackingLink = ({ url, label }) => {
  if (!url) return null;
  return (
    <a className="okl-track-link" href={url} target="_blank" rel="noopener noreferrer">
      {label}
    </a>
  );
};

const KitDetailPanel = ({ order }) => {
  const kit = order?.kit;
  const rows = [
    ["Kit status", displayValue(kit?.status)],
    ["Registration state", displayValue(kit?.registrationState)],
    ["Lab", displayValue(kit?.labName)],
    ["Internal product", displayValue(kit?.internalProductName)],
    ["Requisition ID", displayValue(kit?.requisitionId)],
    ["Tube ID", displayValue(kit?.tubeId)],
    ["Kit created", formatDateTime(kit?.createdAt)],
    ["Registered", formatDateTime(kit?.registrationDate)],
    ["Received", formatDateTime(kit?.receivedDate)],
    ["Kit last updated", formatDateTime(kit?.lastUpdatedAt)],
    ["Test taken", formatDate(kit?.testTakenDate)],
    ["External order ID", displayValue(order?.externalOrderId)],
    ["Vendor order ID", displayValue(order?.vendorOrderId)],
    ["Store source", displayValue(order?.storeSourceType)],
    ["Partner order status", displayValue(order?.partnerOrderStatus)],
    ["Beluga visit status", displayValue(order?.belugaVisitStatus)],
    ["Order last updated", formatDateTime(order?.orderUpdatedAt)],
  ];

  return (
    <div className="okl-detail">
      {!kit && <p className="okl-detail__note">No kit record is linked to this order.</p>}
      <dl className="okl-detail__grid">
        {rows.map(([label, value]) => (
          <div className="okl-detail__item" key={label}>
            <dt className="okl-detail__label">{label}</dt>
            <dd className="okl-detail__value">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const OrderKitLookupPage = () => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [results, setResults] = useState([]);
  const [appliedRange, setAppliedRange] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const { showSnackbar } = useContext(SnackbarContext) || {};
  const primaryInputRef = useRef(null);

  const setFilter = (key) => (event) => {
    const { value } = event.target;
    setFilters((prev) => ({ ...prev, [key]: value }));
    setValidationError(null);
  };

  const hasAnyFilter = Object.entries(filters).some(
    ([key, value]) => key !== "limit" && String(value).trim().length > 0
  );
  const advancedFilterCount = ["partnerId", "pharmacyPartner", "fromDate", "toDate"].filter((key) =>
    String(filters[key]).trim()
  ).length;

  const handleSearch = useCallback(async () => {
    const { valid, error, field } = validateOrderSearchFilters(filters);
    if (!valid) {
      setValidationError(error);
      // Never report an error about a field the disclosure is currently hiding.
      if (ADVANCED_SEARCH_FIELDS.includes(field)) setShowAdvanced(true);
      return;
    }

    setValidationError(null);
    setSearchError(null);
    setLoading(true);
    setHasSearched(true);
    try {
      const response = await ApiService.searchClinicianOrders(buildOrderSearchParams(filters));
      const payload = response?.data || {};
      const orders = Array.isArray(payload.orders) ? payload.orders : [];
      setResults(orders.map((order) => ({ ...order })));
      setAppliedRange(payload.fromDate || payload.toDate ? { from: payload.fromDate, to: payload.toDate } : null);
      if (orders.length === 0) {
        showSnackbar?.({ message: "No orders matched those filters.", severity: "warning", autoHide: 6000 });
      }
    } catch (err) {
      const message = parseAxiosError(err)?.message;
      setResults([]);
      setAppliedRange(null);
      setSearchError(message);
      showSnackbar?.({ message: `Order search failed. ${message}`, severity: "error", autoHide: 8000 });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setResults([]);
    setAppliedRange(null);
    setValidationError(null);
    setSearchError(null);
    setHasSearched(false);
    primaryInputRef.current?.focus();
  };

  const handleFormKeyDown = (event) => {
    if (event.key !== "Enter") return;
    // Buttons inside the form (filter toggle, clear) must keep their own Enter behaviour.
    if ((event.target?.tagName || "").toLowerCase() !== "input") return;
    event.preventDefault();
    if (loading) return;
    handleSearch();
  };

  const resultCount = results.length;

  return (
    <Container fluid className="okl-container">
      <div className="okl-header">
        <h1 className="okl-title">Order &amp; Kit Lookup</h1>
        <p className="okl-subtitle">
          Look up operational order and kit details by ID or partner. This view does not include patient details.
        </p>
      </div>

      <div className="okl-search-section">
        <Form onKeyDown={handleFormKeyDown} className="okl-search-form">
          <div className="okl-primary-row">
            <div className="okl-field">
              <Form.Label className="okl-label" htmlFor="okl-order-id">
                Order ID
              </Form.Label>
              <Form.Control
                id="okl-order-id"
                ref={primaryInputRef}
                type="text"
                placeholder="Exact order ID"
                value={filters.orderId}
                onChange={setFilter("orderId")}
                className="okl-input"
                autoComplete="off"
              />
            </div>
            <div className="okl-field">
              <Form.Label className="okl-label" htmlFor="okl-kit-id">
                Kit ID
              </Form.Label>
              <Form.Control
                id="okl-kit-id"
                type="text"
                placeholder="Exact kit ID"
                value={filters.kitId}
                onChange={setFilter("kitId")}
                className="okl-input"
                autoComplete="off"
              />
            </div>
            <Button
              type="button"
              className="okl-search-btn"
              onClick={handleSearch}
              disabled={loading || !hasAnyFilter}
              aria-label="Search orders"
            >
              {loading ? <BsArrowClockwise size={16} className="okl-spin" /> : <BsSearch size={16} />}
              {loading ? "Searching…" : "Search"}
            </Button>
          </div>

          <div className="okl-controls-row">
            <button
              type="button"
              className="okl-toggle-advanced"
              onClick={() => setShowAdvanced((prev) => !prev)}
              aria-expanded={showAdvanced}
              aria-controls="okl-advanced-filters"
            >
              {showAdvanced ? <BsChevronUp size={14} /> : <BsChevronDown size={14} />}
              {showAdvanced ? "Hide partner filters" : "Partner & date filters"}
              {!showAdvanced && advancedFilterCount > 0 && <span className="okl-badge">{advancedFilterCount}</span>}
            </button>
            {hasAnyFilter && (
              <button type="button" className="okl-clear-btn" onClick={handleClearFilters}>
                <BsXCircle size={14} />
                Clear all
              </button>
            )}
            {hasSearched && !loading && (
              <span className="okl-result-count">
                {resultCount} order{resultCount !== 1 ? "s" : ""} found
              </span>
            )}
          </div>

          {showAdvanced && (
            <div className="okl-advanced-row" id="okl-advanced-filters">
              <div className="okl-field">
                <Form.Label className="okl-label" htmlFor="okl-partner-id">
                  Partner ID
                </Form.Label>
                <Form.Control
                  id="okl-partner-id"
                  type="text"
                  placeholder="e.g. sagebrush"
                  value={filters.partnerId}
                  onChange={setFilter("partnerId")}
                  className="okl-input"
                  autoComplete="off"
                />
              </div>
              <div className="okl-field">
                <Form.Label className="okl-label" htmlFor="okl-pharmacy-partner">
                  Pharmacy Partner
                </Form.Label>
                <Form.Control
                  id="okl-pharmacy-partner"
                  type="text"
                  placeholder="e.g. beluga"
                  value={filters.pharmacyPartner}
                  onChange={setFilter("pharmacyPartner")}
                  className="okl-input"
                  autoComplete="off"
                />
              </div>
              <div className="okl-field">
                <Form.Label className="okl-label" htmlFor="okl-from-date">
                  From date
                </Form.Label>
                <Form.Control
                  id="okl-from-date"
                  type="date"
                  value={filters.fromDate}
                  onChange={setFilter("fromDate")}
                  className="okl-input"
                />
              </div>
              <div className="okl-field">
                <Form.Label className="okl-label" htmlFor="okl-to-date">
                  To date
                </Form.Label>
                <Form.Control
                  id="okl-to-date"
                  type="date"
                  value={filters.toDate}
                  onChange={setFilter("toDate")}
                  className="okl-input"
                />
              </div>
              <div className="okl-field">
                <Form.Label className="okl-label" htmlFor="okl-limit">
                  Limit
                </Form.Label>
                <Form.Control
                  id="okl-limit"
                  type="number"
                  min={ORDER_SEARCH_MIN_LIMIT}
                  max={ORDER_SEARCH_MAX_LIMIT}
                  step={1}
                  value={filters.limit}
                  onChange={setFilter("limit")}
                  className="okl-input"
                />
              </div>
              <p className="okl-advanced-hint">
                Partner searches scan a bounded date range — up to 730 days, defaulting to the last 365.
                Order ID and Kit ID lookups ignore the date range.
              </p>
            </div>
          )}

          {validationError && (
            <div className="okl-validation" role="alert">
              <BsExclamationCircle size={16} className="okl-validation__icon" />
              <span>{validationError}</span>
            </div>
          )}
        </Form>
      </div>

      {hasSearched && appliedRange && (
        <p className="okl-applied-range">
          Showing orders created between <strong>{formatDateTime(appliedRange.from)}</strong> and{" "}
          <strong>{formatDateTime(appliedRange.to)}</strong>.
        </p>
      )}

      <div className="okl-results-section">
        {!hasSearched ? (
          <div className="okl-empty-state">
            <BsBoxSeam size={40} className="okl-empty-icon" />
            <p className="okl-empty-title">Look up an order or kit</p>
            <p className="okl-empty-text">
              Enter an exact <strong>Order ID</strong> or <strong>Kit ID</strong>, or open{" "}
              <strong>Partner &amp; date filters</strong> to browse a partner&apos;s orders.
            </p>
          </div>
        ) : searchError ? (
          <div className="okl-empty-state" role="alert">
            <BsExclamationCircle size={40} className="okl-empty-icon okl-empty-icon--error" />
            <p className="okl-empty-title">Order search failed</p>
            <p className="okl-empty-text">{searchError}</p>
            <Button type="button" className="okl-search-btn okl-retry-btn" onClick={handleSearch} disabled={loading}>
              <BsArrowClockwise size={16} className={loading ? "okl-spin" : undefined} />
              Try again
            </Button>
          </div>
        ) : (
          <MaterialTable
            title=""
            isLoading={loading}
            columns={[
              {
                title: "Order",
                field: "orderId",
                render: (rowData) => (
                  <div className="okl-cell-stack">
                    <span className="okl-cell-primary">{displayValue(rowData.orderId)}</span>
                    <span className="okl-cell-secondary">{displayValue(rowData.productName)}</span>
                  </div>
                ),
                cellStyle: { minWidth: 220, verticalAlign: "top", padding: "12px 16px" },
                headerStyle: { minWidth: 220 },
              },
              {
                title: "Kit ID",
                field: "kitId",
                render: (rowData) => displayValue(rowData.kitId),
                cellStyle: { minWidth: 140, padding: "12px 16px" },
                headerStyle: { minWidth: 140 },
              },
              {
                title: "Status",
                field: "orderStatus",
                render: (rowData) => (
                  <div className="okl-cell-stack">
                    <span className="okl-cell-primary">{displayValue(rowData.orderStatus)}</span>
                    <span className="okl-cell-secondary">{displayValue(rowData.fulfillmentStatus)}</span>
                  </div>
                ),
                cellStyle: { minWidth: 160, verticalAlign: "top", padding: "12px 16px" },
                headerStyle: { minWidth: 160 },
              },
              {
                title: "Partner",
                field: "partnerId",
                render: (rowData) => (
                  <div className="okl-cell-stack">
                    <span className="okl-cell-primary">{displayValue(rowData.partnerId)}</span>
                    <span className="okl-cell-secondary">{displayValue(rowData.pharmacyPartner)}</span>
                  </div>
                ),
                cellStyle: { minWidth: 150, verticalAlign: "top", padding: "12px 16px" },
                headerStyle: { minWidth: 150 },
              },
              {
                title: "Vendor",
                field: "vendor",
                render: (rowData) => displayValue(rowData.vendor),
                cellStyle: { minWidth: 120, padding: "12px 16px" },
                headerStyle: { minWidth: 120 },
              },
              {
                title: "Created",
                field: "orderCreatedAt",
                render: (rowData) => formatDateTime(rowData.orderCreatedAt),
                cellStyle: { minWidth: 170, padding: "12px 16px" },
                headerStyle: { minWidth: 170 },
              },
              {
                title: "Tracking",
                field: "outboundTrackingUrl",
                filtering: false,
                sorting: false,
                render: (rowData) => {
                  if (!rowData.outboundTrackingUrl && !rowData.inboundTrackingUrl) return "—";
                  return (
                    <div className="okl-cell-stack">
                      <TrackingLink url={rowData.outboundTrackingUrl} label="Outbound" />
                      <TrackingLink url={rowData.inboundTrackingUrl} label="Inbound" />
                    </div>
                  );
                },
                cellStyle: { minWidth: 110, verticalAlign: "top", padding: "12px 16px" },
                headerStyle: { minWidth: 110 },
              },
            ]}
            data={results}
            detailPanel={[
              {
                tooltip: "Show kit and fulfillment details",
                render: (rowData) => <KitDetailPanel order={rowData} />,
              },
            ]}
            options={{
              filtering: true,
              exportButton: true,
              exportAllData: true,
              search: false,
              sorting: true,
              draggable: false,
              emptyRowsWhenPaging: false,
              pageSize: 25,
              pageSizeOptions: [10, 25, 50, 100],
              toolbarButtonAlignment: "right",
              maxBodyHeight: "calc(100vh - 360px)",
              headerStyle: {
                position: "sticky",
                top: 0,
                backgroundColor: "#f9fafb",
                color: "#6b7280",
                fontWeight: "600",
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                borderBottom: "2px solid #e5e7eb",
                padding: "10px 16px",
              },
              rowStyle: (_, index) => ({
                backgroundColor: index % 2 === 0 ? "#fff" : "#f9fafb",
                fontSize: "0.9rem",
                color: "#1f2937",
              }),
            }}
            localization={{
              body: {
                emptyDataSourceMessage: (
                  <div className="okl-table-empty">
                    No orders match those filters. Check the ID, or widen the partner date range.
                  </div>
                ),
              },
              toolbar: { exportTitle: "Export", exportAriaLabel: "Export orders" },
            }}
          />
        )}
      </div>
    </Container>
  );
};

export default OrderKitLookupPage;
